// ============================================================================
// QuoteBrainComparePanel: Adopt or Keep mine for a Quote Brain result that was
// held aside (docs/quote-brain/BUILD-PLAN.md slice WRITE, Peter 2026-10-26).
// ----------------------------------------------------------------------------
// A result is held, never written, only when the job already had a quote (a
// re-run the builder started, or a revision) or items were added while the
// run worked (Peter's O1, 2026-10-28). This panel sets the two lists side by
// side:
//   - the current list, each row marked with what Adopt does to it: kept
//     (locked, edited, added) or replaced, with the reason;
//   - the Quote Brain's list, a row your kept row already covers marked as
//     left out (Adopt never prints two items for the same work), and the
//     total Adopt would leave;
//   - the open questions and findings in plain words, each once, and the past
//     quotes the run used. The run's spend and turns are not shown here.
// Adopt writes the brain's list with the kept rows. Keep mine leaves the list
// exactly as it is and keeps the result on record. Nothing happens silently.
// ============================================================================

import React from 'react';
import type { QuoteBrainComparison, QuoteBrainRowMark } from '../../services/quote-brain-runs';
import { builderText } from '../../../backend/src/shared/quote-brain/builder-text.js';
import './quote-brain.css';

const REASON: Record<QuoteBrainRowMark, string> = {
    locked: 'Kept: you locked its price',
    edited: 'Kept: you edited it',
    added: 'Kept: you added it',
    unchanged: 'Replaced: it is as the AI wrote it',
    'not-tracked': 'Replaced',
    statutory: 'Replaced: the new quote brings its own',
    'statutory-typed': 'Kept: you typed its amount',
};

const aud = (n: number) => `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function laneLabel(lane: any): string {
    if (typeof lane === 'string') return lane;
    return String(lane?.title || lane?.quoteNumber || lane?.label || lane?.id || '');
}

export interface QuoteBrainComparePanelProps {
    comparison: QuoteBrainComparison;
    busy: boolean;
    onAdopt: () => void;
    onKeepMine: () => void;
    onClose: () => void;
}

interface Line { key: string; text: string; detail: string; where: string[]; tone: 'danger' | 'attn' | 'plain' }

/**
 * The questions and findings as the builder reads them: plain words, each
 * once. A line that reads the same for several items is one line naming them.
 */
export function compareLines(comparison: Pick<QuoteBrainComparison, 'candidate' | 'findings' | 'confirms'>): Line[] {
    const labels = new Map<string, string>();
    for (const r of comparison.candidate?.rows || []) if (r.itemId) labels.set(r.itemId, `Item ${r.code}`);
    const itemLabel = (id: string) => labels.get(id) || '';
    const lines: Line[] = [];
    const byKey = new Map<string, Line>();
    const add = (tone: Line['tone'], text: string, detail: string, itemId?: string) => {
        const plain = builderText(text, { itemLabel });
        const more = builderText(detail, { itemLabel });
        if (!plain) return;
        const key = `${tone}|${plain.toLowerCase()}|${more.toLowerCase()}`;
        const where = itemId ? itemLabel(itemId) : '';
        const seen = byKey.get(key);
        if (seen) {
            if (where && !seen.where.includes(where)) seen.where.push(where);
            return;
        }
        const line: Line = { key, text: plain, detail: more, where: where ? [where] : [], tone };
        byKey.set(key, line);
        lines.push(line);
    };
    const findings = comparison.findings || [];
    for (const f of findings.filter((x) => x.blocking)) add('danger', f.message, '', f.itemId);
    for (const c of comparison.confirms || []) add('attn', c.question, c.assumption ? `The quote assumes: ${c.assumption}` : '', c.itemId);
    for (const f of findings.filter((x) => !x.blocking)) add('plain', f.message, '', f.itemId);
    return lines;
}

export function QuoteBrainComparePanel({ comparison, busy, onAdopt, onKeepMine, onClose }: QuoteBrainComparePanelProps) {
    const { current, candidate, afterAdopt } = comparison;
    const kept = current.rows.filter((r) => r.keep).length;
    const notTracked = current.rows.some((r) => r.reason === 'not-tracked');
    const covered = candidate.rows.filter((r) => r.coveredByYours).length;
    const replaces = [comparison.replaces?.workMethod ? 'Work Method' : '', comparison.replaces?.costingSheet ? 'costing sheet' : ''].filter(Boolean);
    const lines = compareLines(comparison);
    const count = (tone: Line['tone']) => lines.filter((l) => l.tone === tone).length;
    const lanes = (comparison.lanesUsed || []).map(laneLabel).filter(Boolean);
    return (
        <section className="qb-compare" aria-label="Compare QuoteMaker's quote with yours" data-testid="quote-brain-compare">
            <div className="qb-compare__head">
                <span className="qb-compare__title">Compare QuoteMaker's quote</span>
                {comparison.holdReason && <span className="qb-compare__why">{comparison.holdReason}</span>}
                <button type="button" className="qm-inline-action-btn qm-btn qm-btn--secondary qb-compare__close" onClick={onClose}>Later</button>
            </div>
            {notTracked && (
                <div className="qb-compare__note">
                    This job has no record of the rows the AI wrote, so Adopt keeps only the rows you locked in Stage 4. Lock a row there first to keep it.
                </div>
            )}
            {covered > 0 && (
                <div className="qb-compare__note" data-testid="quote-brain-compare-covered">
                    {covered === 1 ? 'One of your kept rows covers' : 'Your kept rows cover'} the same work as {covered === 1 ? 'a row' : `${covered} rows`} of QuoteMaker's, so Adopt keeps yours and leaves {covered === 1 ? 'that row' : 'those rows'} out.
                </div>
            )}
            {replaces.length > 0 && (
                <div className="qb-compare__note">
                    Adopt also clears this job's {replaces.join(' and ')}, which describe your current list.
                </div>
            )}
            {comparison.adoptBlocked && (
                <div className="qb-compare__note" role="status" data-testid="quote-brain-adopt-blocked">{comparison.adoptBlocked.message}</div>
            )}
            <div className="qb-compare__cols">
                <div className="qb-compare__col" data-testid="quote-brain-compare-current">
                    <div className="qb-compare__colhead">
                        <span>Your quote{current.gstInclusive === false ? ' (ex GST)' : ''}</span>
                        <span className="qb-compare__total">{aud(current.total)}</span>
                    </div>
                    <ol className="qb-compare__rows">
                        {current.rows.length === 0 && <li className="qb-compare__empty">No items yet.</li>}
                        {current.rows.map((r) => (
                            <li key={r.id || r.code} className={`qb-compare__row${r.keep ? ' qb-compare__row--kept' : ' qb-compare__row--replaced'}`}>
                                <span className="qb-compare__rowtitle">{r.title || 'Untitled item'}</span>
                                <span className="qb-compare__amount">{aud(r.amount)}</span>
                                <span className="qb-compare__mark">{REASON[r.reason] || r.reason}</span>
                            </li>
                        ))}
                    </ol>
                </div>
                <div className="qb-compare__col" data-testid="quote-brain-compare-candidate">
                    <div className="qb-compare__colhead">
                        <span>QuoteMaker's quote</span>
                        <span className="qb-compare__total">{aud(candidate.total)}</span>
                    </div>
                    <ol className="qb-compare__rows">
                        {candidate.rows.map((r) => (
                            <li key={r.id} className={`qb-compare__row${r.coveredByYours ? ' qb-compare__row--replaced' : ''}`}>
                                <span className="qb-compare__rowtitle">{r.title || 'Untitled item'}</span>
                                <span className="qb-compare__amount">{aud(r.amount)}</span>
                                {r.coveredByYours && <span className="qb-compare__mark">Left out: your row covers this work</span>}
                            </li>
                        ))}
                    </ol>
                    {candidate.optional.length > 0 && (
                        <div className="qb-compare__optional">
                            Priced in the Note, not the total: {candidate.optional.map((o) => `${o.title} ${aud(o.amount)}`).join('; ')}
                        </div>
                    )}
                </div>
            </div>
            <div className="qb-compare__facts">
                <span className="qm-chip">After Adopt {aud(afterAdopt.total)}{kept ? `, with ${kept} of your rows kept` : ''}</span>
                {lines.length > 0 && (
                    <span className={`qm-chip ${count('danger') ? 'qm-chip--danger' : 'qm-chip--attn'}`}>
                        {[count('danger') ? `${count('danger')} to fix` : '', count('attn') ? `${count('attn')} to confirm` : '', count('plain') ? `${count('plain')} to check` : ''].filter(Boolean).join(', ')}
                    </span>
                )}
                {lanes.length > 0 && <span className="qm-chip">Past quotes used: {lanes.join(', ')}</span>}
            </div>
            {lines.length > 0 && (
                <ul className="qb-findings__list qb-compare__findings" data-testid="quote-brain-compare-lines">
                    {lines.map((l) => (
                        <li key={l.key} className={`qb-findings__row qb-findings__row--${l.tone}`}>
                            {l.where.length > 0 && <span className="qm-chip qb-findings__where">{l.where.join(', ')}</span>}
                            <span className="qb-findings__body">
                                <span className="qb-findings__text">{l.tone === 'attn' ? `Confirm: ${l.text}` : l.text}</span>
                                {l.detail && <span className="qb-findings__detail">{l.detail}</span>}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            <div className="qb-compare__actions">
                <button type="button" className="qm-inline-action-btn qm-btn qm-btn--primary" disabled={busy || !!comparison.adoptBlocked} onClick={onAdopt} data-testid="quote-brain-adopt">Adopt</button>
                <button type="button" className="qm-inline-action-btn qm-btn qm-btn--secondary" disabled={busy} onClick={onKeepMine} data-testid="quote-brain-keep-mine">Keep mine</button>
            </div>
        </section>
    );
}

export default QuoteBrainComparePanel;
